import React, { useEffect } from 'react';
import {
  ErrorComponentType,
  ErrorDisplayButton,
  ErrorDisplayType,
  useErrorDisplay,
} from 'shared/hooks/errors/useErrorDisplay';
import { useErrorDisplayStrings } from 'shared/hooks/errors/useErrorDisplayStrings';
import { useRetryWithBackoff } from 'shared/hooks/errors/useRetryWithBackoff';
import ErrorScreen, {
  ERROR_SCREEN_TEST_ID_PREFIX,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import PageDownErrorMessage, {
  PAGE_DOWN_ERROR_MESSAGE_TEST_ID_PREFIX,
} from 'shared/patterns/vxl/errors/PageDownErrorMessage/PageDownErrorMessage';
import { Maybe } from 'shared/utils/types';

interface ErrorDisplayProps {
  error: Maybe<unknown>;
  displayType?: ErrorDisplayType;
  messageHeadline?: string;
  messageBody?: string;
  primaryButton?: ErrorDisplayButton;
  secondaryButton?: ErrorDisplayButton;
  hasPageHeader?: boolean;
}

/**
 * Universal error display — one component for all error scenarios.
 *
 * Renders the appropriate error UI for the classified error and display
 * type. Retry actions on server errors automatically apply exponential
 * backoff to avoid hammering an unhealthy backend.
 *
 * @example
 * // Tab error with retry
 * <ErrorDisplay
 *   error={error}
 *   displayType="tab"
 *   primaryButton={{ title: 'Try again', onPress: refetch }}
 * />
 *
 * @example
 * // Inline feature error
 * <ErrorDisplay
 *   error={error}
 *   displayType="inline"
 *   primaryButton={{ title: 'Go back', onPress: () => router.back() }}
 * />
 */
export const ErrorDisplay = ({
  error,
  displayType = ErrorDisplayType.Inline,
  primaryButton,
  ...options
}: ErrorDisplayProps) => {
  const strings = useErrorDisplayStrings();
  const { retry, reset, isRetrying, countdown } = useRetryWithBackoff();

  useEffect(() => {
    if (!error) {
      reset();
    }
  }, [error, reset]);

  const errorDisplay = useErrorDisplay(error, {
    displayType,
    ...options,
    primaryButton,
  });

  if (!errorDisplay) {
    return null;
  }

  const shouldBackoff =
    errorDisplay.classifiedType === 'server' ||
    errorDisplay.classifiedType === 'graphql';

  const applyBackoff = (
    button: ErrorDisplayButton,
  ): ErrorDisplayButton & { title: string } => ({
    title:
      countdown > 0
        ? strings.waitCountdownTemplate(countdown)
        : (button.title ?? strings.tryAgainButtonTitle),
    onPress: shouldBackoff ? () => retry(button.onPress) : button.onPress,
    isDisabled: button.isDisabled || isRetrying,
    isLoading: button.isLoading || (isRetrying && countdown === 0),
  });

  if (errorDisplay.componentType === ErrorComponentType.ErrorScreen) {
    const props = errorDisplay.errorScreenProps;
    return (
      <ErrorScreen
        {...props}
        testID={`${ERROR_SCREEN_TEST_ID_PREFIX}-${errorDisplay.classifiedType}`}
        primaryButton={
          props.primaryButton ? applyBackoff(props.primaryButton) : undefined
        }
      />
    );
  }

  const {
    primaryOnPress,
    primaryButtonText,
    primaryButtonIsDisabled,
    primaryButtonIsLoading,
    ...restPageDownProps
  } = errorDisplay.pageDownProps;

  const normalizedButton = applyBackoff({
    title: primaryButtonText,
    onPress: primaryOnPress,
    isDisabled: primaryButtonIsDisabled,
    isLoading: primaryButtonIsLoading,
  });

  return (
    <PageDownErrorMessage
      {...restPageDownProps}
      testID={`${PAGE_DOWN_ERROR_MESSAGE_TEST_ID_PREFIX}-${errorDisplay.classifiedType}`}
      primaryOnPress={normalizedButton.onPress}
      primaryButtonText={normalizedButton.title}
      primaryButtonIsDisabled={normalizedButton.isDisabled}
      primaryButtonIsLoading={normalizedButton.isLoading}
    />
  );
};
